import { Button } from "@dropbox/dig-components/buttons";
import { Modal } from "@dropbox/dig-components/modal";
import { TextArea } from "@dropbox/dig-components/text_fields";
import { Text } from "@dropbox/dig-components/typography";
import useConversation from "@mirage/service-conversation/hooks/useConversation";
import { convertDocumentInfoToChatSource } from "@mirage/service-conversation/utils/convertDocumentInfoToChatSource";
import { copyToClipboard } from "@mirage/service-platform-actions";
import type { ScoredResult } from "@mirage/service-search/service";
import type { SearchResult } from "@mirage/shared/search/search-result";
import { TwoColumnGrid } from "@mirage/shared/two-column-grid/TwoColumnGrid";
import i18n from "@mirage/translations";
import { searchSessionManager } from "@mirage/webapp/analytics/sessionManagers/sessionManagers";
import { useAtom } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { ConversationMessages } from "../Chat/components/chat/ConversationMessages/ConversationMessages";
import type { ChatConversationMessage } from "../Chat/types";
import { activeConversationIdAtom } from "../MultiAnswersCard/useInitialConversation";
import styles from "./SearchPageDataRetrieval.module.css";
import type {
  AIResponse,
  SearchPageDataResponse,
  SearchPageDataRetrievalResult,
} from "./searchPageDataRetrieval";
import {
  answerRetrievalCategoryResultsAtom,
  callLLM,
  PROMPT_PREFIX_DYNAMIC,
} from "./searchPageDataRetrieval";

interface SearchPageDataRetrievalProps {
  searchResults: ScoredResult[];
  query: string;
}

export const DynamicSearchPageResult: React.FC<
  SearchPageDataRetrievalProps
> = ({ searchResults, query }) => {
  const [dataModalOpen, setDataModalOpen] = useState(false);
  const [gathering, setGathering] = useState(true);
  const [responseLoading, setResponseLoading] = useState(false);
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [aiResponse, setAiResponse] = useState<AIResponse>();
  const [responseAsText, setResponseAsText] = useState<string>("");
  const [activeConversationId] = useAtom(activeConversationIdAtom);
  const [categoryResults] = useAtom(answerRetrievalCategoryResultsAtom);
  const conversation = useConversation(activeConversationId);
  const [invalidJsonError, setInvalidJsonError] = useState(false);
  const [, setUuidToResultsMap] = useState<Record<string, SearchResult>>({});

  const onCloseDataModal = () => {
    setDataModalOpen(false);
  };
  const onClosePromptModal = () => {
    setPromptModalOpen(false);
  };
  const markdownRef = useRef<HTMLDivElement | null>(null);

  const firstResponseFromConversation = useMemo(
    () =>
      conversation?.messages?.find(
        (message) => message.messageType?.case === "assistantMessage",
      ),
    [conversation?.messages],
  );

  const firstResponseFromConversationText = useMemo(
    () => firstResponseFromConversation?.messageType?.value?.text,
    [firstResponseFromConversation],
  );

  const [jsonResponse, setJsonResponse] = useState<string>("");
  const [prompt, setPrompt] = useState<string>(PROMPT_PREFIX_DYNAMIC);

  const searchResultsSlice = useMemo(() => {
    return searchResults.slice(0, 10);
  }, [searchResults]);

  const gatherData = () => {
    let currIdNumber = 0;
    const searchResultMap: Record<string, SearchResult> = {};
    searchResults.forEach((result) => {
      searchResultMap[result.uuid] = result;
    });
    const idNoResultMap: Record<number, SearchResult> = {};
    Object.entries(categoryResults).forEach(([, results]) => {
      results.forEach((result) => {
        if (!searchResultMap[result.uuid]) {
          searchResultMap[result.uuid] = result;
        }
      });
    });
    setUuidToResultsMap(searchResultMap);
    const response: SearchPageDataResponse = {
      conversationResponse: markdownRef.current?.textContent ?? "",
      conversationResponseMarkdown: firstResponseFromConversationText ?? "",
      conversationCitations:
        firstResponseFromConversation?.messageType.case === "assistantMessage"
          ? convertDocumentInfoToChatSource(
              firstResponseFromConversation?.messageType?.value?.cited,
            )
          : [],
      conversationFollowUps: conversation?.suggestedQueries ?? [],
      searchResults: searchResultsSlice.slice(0, 10).map((result) => {
        const idNo = currIdNumber++;
        idNoResultMap[idNo] = result;
        return {
          title: result.title,
          description: result.description,
          text: result.highlights?.body?.text?.[0] ?? "",
          fileType: result.connectorInfo.displayName,
          creator: result.creator?.displayName,
          lastUpdated: result.providerUpdateAtMs,
          relevanceScore: result.relevanceScore,
          channel: result.channel,
          uuid: result.uuid,
          id_no: idNo,
        };
      }),
      categoryResults: {
        ...Object.entries(categoryResults).reduce(
          (acc, [categoryId, results]) => {
            acc[categoryId] = results.map((result) => {
              const idNo = currIdNumber++;
              idNoResultMap[idNo] = result;
              return {
                title: result.title,
                description: result.description,
                fileType: result.connectorInfo.displayName,
                text: result.highlights?.body?.text?.[0] ?? "",
                creator: result.creator?.displayName,
                lastUpdated: result.providerUpdateAtMs,
                relevanceScore: result.relevanceScore,
                channel: result.channel,
                uuid: result.uuid,
                id_no: idNo,
              };
            });
            return acc;
          },
          {} as Record<string, SearchPageDataRetrievalResult[]>,
        ),
      },
    };
    setJsonResponse(JSON.stringify(response, null, 2));
    return response;
  };

  const sendNewPrompt = () => {
    onClosePromptModal();
    setResponseAsText("");
    startLLM();
  };

  useEffect(() => {
    setGathering(true);
    setResponseAsText("");
    setAiResponse(undefined);
  }, [query]);

  const startLLM = () => {
    setInvalidJsonError(false);
    setResponseLoading(true);
    const response = gatherData();
    const resp = callLLM({
      query: query,
      data: response,
      prefix: prompt,
    });
    const val = "";
    setConversationMessages((prev) => [
      ...prev,
      {
        type: "message",
        role: "assistant",
        text: "",
      },
    ]);
    resp.then((res) => {
      res.subscribe({
        next: (val) => {
          setConversationMessages((prev) => {
            const next = [...prev];
            next[next.length - 1] = {
              ...next[next.length - 1],
              type: "message",
              role: "assistant",
              text: val.answer.text,
            };
            return next;
          });
        },
        complete: () => {
          try {
            const responseToJson: AIResponse = JSON.parse(val);
            setAiResponse(responseToJson);
          } catch (e) {
            // setInvalidJsonError(true);
            setAiResponse(undefined);
          }
          setResponseLoading(false);
        },
      });
    });
  };

  useEffect(() => {
    if (firstResponseFromConversation?.status === "success") {
      setGathering(false);
      startLLM();
    }
  }, [firstResponseFromConversation]);

  const [conversationMessages, setConversationMessages] = useState<
    ChatConversationMessage[]
  >([]);

  return (
    <>
      <div className={styles.answerRetrievalContainer}>
        <div ref={markdownRef} className={styles.markdownContainer}>
          <ReactMarkdown>{firstResponseFromConversationText}</ReactMarkdown>
        </div>
        <Button variant="outline" onClick={() => setPromptModalOpen(true)}>
          Edit Prompt
        </Button>
        <Button variant="outline" onClick={() => setDataModalOpen(true)}>
          View Data
        </Button>
      </div>
      <Modal
        open={promptModalOpen}
        onRequestClose={onClosePromptModal}
        shouldCloseOnOverlayClick={true}
        withCloseButton={i18n.t("close_modal_aria")}
        isCentered
        width="large"
      >
        <Modal.Header>
          <Modal.Title>Input data</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <Text tagName="p" size="small" isBold>
              Results
            </Text>
            <TextArea
              rows={14}
              resizable="vertical"
              onChange={(e) => setPrompt(e.currentTarget.value)}
              value={prompt}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => sendNewPrompt()}>
            Send
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        open={dataModalOpen}
        onRequestClose={onCloseDataModal}
        shouldCloseOnOverlayClick={true}
        withCloseButton={i18n.t("close_modal_aria")}
        isCentered
        width="large"
      >
        <Modal.Header>
          <Modal.Title>Input data</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <Text tagName="p" size="small" isBold>
              Results
            </Text>
            <TextArea
              rows={14}
              readOnly
              resizable="vertical"
              onChange={(e) => setJsonResponse(e.currentTarget.value)}
              value={jsonResponse}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="opacity" onClick={onCloseDataModal}>
            {i18n.t("close")}
          </Button>
          <Button
            variant="primary"
            onClick={() =>
              copyToClipboard(JSON.stringify(jsonResponse, null, 2))
            }
          >
            {i18n.t("copy")}
          </Button>
        </Modal.Footer>
      </Modal>
      {gathering && (
        <div>
          <Text tagName="p" size="small">
            Gathering results...
          </Text>
        </div>
      )}
      {responseLoading && (
        <div>
          <Text tagName="p" size="small">
            Loading response...
          </Text>
        </div>
      )}
      {!gathering && (
        <TwoColumnGrid
          mainChildren={
            invalidJsonError ? (
              <Text tagName="p" size="small" isBold>
                Invalid JSON response from AI. Please check the response format.
              </Text>
            ) : (
              <>
                <ConversationMessages
                  variant="default"
                  noPadding
                  messages={conversationMessages}
                  isWaitingForResponse={false}
                  isGeneratingResponse={false}
                  isChatTrialNotificationVisible={false}
                  progressComponent={undefined}
                  onRemoveSource={() => {}}
                  isLastMessageFollowUpSuggestions={false}
                  artifacts={[]}
                  logComposeEvent={() => {}}
                  onOpenArtifact={() => {}}
                  hideFirstUserMessage={true}
                  hideSources
                  conversationId={conversation?.serverConversationId || ""}
                  searchSessionId={searchSessionManager.getSessionIdOrUndefined()}
                  assistRequestId={conversation?.assistRequestId}
                  spanId={conversation?.spanId}
                />
              </>
            )
          }
          sidebarChildren={
            <pre style={{ height: "100%", width: "100%", overflow: "auto" }}>
              {aiResponse
                ? JSON.stringify(aiResponse, null, 2)
                : responseAsText}
            </pre>
          }
        ></TwoColumnGrid>
      )}
    </>
  );
};
